import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../../shared/api/apiClient";
import { getUserPermissions } from "../../../../src/shared/lib/auth/authHelper";
import "./EmployeesPage.css";

function Employees() {
  const { t } = useTranslation();
  const permissions = getUserPermissions() || [];
  const canEditUsers = permissions.includes("users.edit");
  const canCreateUsers = permissions.includes("users.create");
  const canChangeAnyUserPassword = permissions.includes("users.change_password.any");

  const [clients, setClients] = useState([]);
  const [assignableRoles, setAssignableRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    role_id: "",
    country: "",
    city: "",
    description: "",
    date_of_birth: "",
    hashed_pass: "",
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [userId, setUserId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [clientsPerPage, setClientsPerPage] = useState(5);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    void refreshData();
  }, []);

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const response = await api.get("/auth/user/all", {
        params: { skip: 0, limit: 100 },
      });

      const nextClients = Array.isArray(response.data) ? response.data : [];
      setClients(nextClients);
    } catch (err) {
      console.error(t("error_fetching_users"), err);
    } finally {
      setLoadingClients(false);
    }
  };

  const fetchRoles = async () => {
    setLoadingRoles(true);

    try {
      const [assignableResponse, allRolesResponse] = await Promise.all([
        api.get("/auth/roles/assignable-for-me"),
        api.get("/auth/roles/all", {
          params: { skip: 0, limit: 200 },
        }),
      ]);

      const assignableData = Array.isArray(assignableResponse.data)
        ? assignableResponse.data
        : [];
      const allRolesData = Array.isArray(allRolesResponse.data)
        ? allRolesResponse.data
        : [];

      setAssignableRoles(assignableData);
      setAllRoles(allRolesData);

      setFormData((prev) => {
        if (prev.role_id) return prev;

        const workerRole =
          assignableData.find(
            (role) => String(role.role_name || "").trim().toLowerCase() === "worker"
          ) || assignableData[0];

        return {
          ...prev,
          role_id: workerRole ? String(workerRole.id) : "",
        };
      });
    } catch (err) {
      console.error("Failed to fetch roles", err);

      try {
        const allRolesResponse = await api.get("/auth/roles/all", {
          params: { skip: 0, limit: 200 },
        });

        const allRolesData = Array.isArray(allRolesResponse.data)
          ? allRolesResponse.data
          : [];

        setAssignableRoles([]);
        setAllRoles(allRolesData);
      } catch (innerErr) {
        console.error("Failed to fetch fallback roles", innerErr);
        setAssignableRoles([]);
        setAllRoles([]);
      }
    } finally {
      setLoadingRoles(false);
    }
  };

  const refreshData = async () => {
    await Promise.all([fetchClients(), fetchRoles()]);
  };

  const defaultRoleId = useMemo(() => {
    const workerRole =
      assignableRoles.find(
        (role) => String(role.role_name || "").trim().toLowerCase() === "worker"
      ) || assignableRoles[0];

    return workerRole ? String(workerRole.id) : "";
  }, [assignableRoles]);

  const selectableRoles = useMemo(() => {
    const map = new Map();

    assignableRoles.forEach((role) => {
      map.set(String(role.id), role);
    });

    if (isEditing && formData.role_id) {
      const existingRole =
        allRoles.find((role) => String(role.id) === String(formData.role_id)) || null;

      if (existingRole && !map.has(String(existingRole.id))) {
        map.set(String(existingRole.id), existingRole);
      }
    }

    return Array.from(map.values());
  }, [assignableRoles, allRoles, isEditing, formData.role_id]);

  const generatePassword = (length = 12) => {
    const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const lower = "abcdefghijkmnopqrstuvwxyz";
    const digits = "23456789";
    const symbols = "!@#$%^&*";
    const all = upper + lower + digits + symbols;

    let result = "";
    result += upper[Math.floor(Math.random() * upper.length)];
    result += lower[Math.floor(Math.random() * lower.length)];
    result += digits[Math.floor(Math.random() * digits.length)];
    result += symbols[Math.floor(Math.random() * symbols.length)];

    for (let i = result.length; i < length; i++) {
      result += all[Math.floor(Math.random() * all.length)];
    }

    return result
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");
  };

  const handleGeneratePassword = () => {
    const nextPassword = generatePassword(12);
    setFormData((prev) => ({
      ...prev,
      hashed_pass: nextPassword,
    }));
    setShowPassword(true);
  };

  const resetForm = () => {
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      role_id: defaultRoleId,
      country: "",
      city: "",
      description: "",
      date_of_birth: "",
      hashed_pass: "",
    });
    setShowPassword(false);
  };

  useEffect(() => {
    if (!isEditing && !formData.role_id && defaultRoleId) {
      setFormData((prev) => ({
        ...prev,
        role_id: defaultRoleId,
      }));
    }
  }, [defaultRoleId, formData.role_id, isEditing]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    try {
      const normalizedRoleId = formData.role_id ? Number(formData.role_id) : null;

      if (!normalizedRoleId) {
        setError(t("error_generic"));
        return;
      }

      const updatedData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        role_id: normalizedRoleId,
        country: formData.country,
        city: formData.city,
        date_of_birth: formData.date_of_birth || null,
        description: formData.description || "",
      };

      if (isEditing && userId) {
        await api.put(`/auth/user/edit/${userId}`, updatedData);

        if (canChangeAnyUserPassword && String(formData.hashed_pass || "").trim()) {
          await api.put(`/auth/user/password/${userId}`, {
            new_password: String(formData.hashed_pass || "").trim(),
          });
        }

        setSuccess(t("user_updated"));
      } else {
        await api.post("/auth/employee/add", {
          ...updatedData,
          hashed_pass: formData.hashed_pass,
        });
        setSuccess(t("user_added"));
      }

      resetForm();
      setIsEditing(false);
      setUserId(null);
      await refreshData();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          t("error_generic")
      );
      console.error(err);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (client) => {
    setIsEditing(true);
    setUserId(client.userId);

    let resolvedRoleId = "";

    const matchedRoleByName = allRoles.find(
      (role) =>
        String(role.role_name || "").trim().toLowerCase() ===
        String(client.role || "").trim().toLowerCase()
    );

    if (matchedRoleByName) {
      resolvedRoleId = String(matchedRoleByName.id);
    }

    setFormData({
      first_name: client.first_name || "",
      last_name: client.last_name || "",
      email: client.email || "",
      phone: client.phone || "",
      country: client.country || "",
      role_id: resolvedRoleId || defaultRoleId,
      city: client.city || "",
      date_of_birth: client.date_of_birth || "",
      description: client.description || "",
      hashed_pass: "",
    });

    setShowPassword(false);
    setError("");
    setSuccess("");
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setUserId(null);
    setError("");
    setSuccess("");
    resetForm();
  };

  const filteredClients = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    if (!query) {
      return clients;
    }

    return clients.filter((client) => {
      return (
        String(client.first_name || "").toLowerCase().includes(query) ||
        String(client.last_name || "").toLowerCase().includes(query) ||
        String(client.email || "").toLowerCase().includes(query) ||
        String(client.phone || "").toLowerCase().includes(query) ||
        String(client.created_at || "").toLowerCase().includes(query) ||
        String(client.description || "").toLowerCase().includes(query) ||
        String(client.last_date_connection || "").toLowerCase().includes(query) ||
        String(client.role || "").toLowerCase().includes(query) ||
        String(client.country || "").toLowerCase().includes(query) ||
        String(client.city || "").toLowerCase().includes(query) ||
        String(client.date_of_birth || "").toLowerCase().includes(query)
      );
    });
  }, [clients, searchQuery]);

  const sortedClients = useMemo(() => {
    const items = [...filteredClients];

    if (!sortConfig.key) {
      return items;
    }

    items.sort((a, b) => {
      const av = a?.[sortConfig.key];
      const bv = b?.[sortConfig.key];

      if (av == null && bv == null) return 0;
      if (av == null) return sortConfig.direction === "asc" ? -1 : 1;
      if (bv == null) return sortConfig.direction === "asc" ? 1 : -1;

      const aValue = String(av).toLowerCase();
      const bValue = String(bv).toLowerCase();

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return items;
  }, [filteredClients, sortConfig]);

  const sortData = (key) => {
    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const indexOfLastClient = currentPage * clientsPerPage;
  const indexOfFirstClient = indexOfLastClient - clientsPerPage;
  const currentClients = sortedClients.slice(indexOfFirstClient, indexOfLastClient);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleClientsPerPageChange = (event) => {
    setClientsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const showingText = t("showing_entries", {
    start: sortedClients.length === 0 ? 0 : (currentPage - 1) * clientsPerPage + 1,
    end: Math.min(currentPage * clientsPerPage, sortedClients.length),
    total: sortedClients.length,
  });

  const totalPages = Math.ceil(sortedClients.length / clientsPerPage);

  return (
    <div className="container-fluid px-0 employeesPage">
      <div className="employeesPageHeader">
        <h1>{t("employees")}</h1>

        <button
          className="btn btn-primary employeesHeaderRefreshBtn"
          onClick={() => {
            void refreshData();
          }}
          disabled={loadingClients || loadingRoles}
        >
          {t("refresh")}
        </button>
      </div>
      {(canEditUsers || canCreateUsers) && (
        <div className="card mb-3 employeesFormCard">
          <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div>
              {isEditing
                ? canEditUsers && (
                    <div style={{ fontWeight: 800 }}>
                      {t("update_employee")}
                    </div>
                  )
                : canCreateUsers && (
                    <div style={{ fontWeight: 800 }}>
                      {t("add_employee")}
                    </div>
                  )}
              <div className="text-muted small">{t("employees_form_hint")}</div>
            </div>

            {isEditing && (
              <span className="badge bg-warning text-dark" style={{ fontWeight: 800 }}>
                {t("edit")}
              </span>
            )}
          </div>

          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-12 col-md-6 col-lg-3">
                  <label className="form-label">{t("first_name")}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-3">
                  <label className="form-label">{t("last_name")}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-3">
                  <label className="form-label">{t("email")}</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    readOnly={isEditing}
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-3">
                  <label className="form-label">{t("phone")}</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-3">
                  <label className="form-label">{t("role")}</label>
                  <select
                    className="form-select"
                    name="role_id"
                    value={formData.role_id}
                    onChange={handleChange}
                    required
                    disabled={loadingRoles}
                  >
                    {selectableRoles.length === 0 ? (
                      <option value="">{t("loading")}...</option>
                    ) : (
                      selectableRoles.map((role) => (
                        <option key={role.id} value={String(role.id)}>
                          {role.role_name}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="col-12 col-md-6 col-lg-3">
                  <label className="form-label">{t("country")}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-3">
                  <label className="form-label">{t("city")}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-3">
                  <label className="form-label">{t("date_of_birth")}</label>
                  <input
                    type="date"
                    className="form-control"
                    name="date_of_birth"
                    value={formData.date_of_birth || ""}
                    onChange={handleChange}
                  />
                </div>

                {!isEditing && (
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="form-label">{t("password")}</label>
                    <div className="employeesPasswordDesktop">
                      <div className="input-group">
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control"
                          name="hashed_pass"
                          value={formData.hashed_pass || ""}
                          onChange={handleChange}
                          required
                        />

                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowPassword((prev) => !prev)}
                          title={
                            showPassword
                              ? (t("hide_password") || "Hide password")
                              : (t("show_password") || "Show password")
                          }
                        >
                          {showPassword ? (
                            <i className="lni lni-locked-2"></i>
                          ) : (
                            <i className="lni lni-unlocked-2"></i>
                          )}
                        </button>

                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={handleGeneratePassword}
                          title={t("generate_password") || "Generate password"}
                        >
                          <i className="lni lni-refresh-circle-1-clockwise"></i>
                        </button>
                      </div>
                    </div>

                    <div className="employeesPasswordMobile">
                      <div className="employeesPasswordBox">
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control employeesPasswordInput"
                          name="hashed_pass"
                          value={formData.hashed_pass || ""}
                          onChange={handleChange}
                          required
                        />

                        <div className="employeesPasswordActions">
                          <button
                            type="button"
                            className="employeesPasswordActionBtn"
                            onClick={() => setShowPassword((prev) => !prev)}
                            title={
                              showPassword
                                ? (t("hide_password") || "Hide password")
                                : (t("show_password") || "Show password")
                            }
                          >
                            <i
                              className={
                                showPassword
                                  ? "lni lni-locked-2"
                                  : "lni lni-unlocked-2"
                              }
                            ></i>
                            <span>
                              {showPassword
                                ? (t("hide") || "Hide")
                                : (t("show") || "Show")}
                            </span>
                          </button>

                          <button
                            type="button"
                            className="employeesPasswordActionBtn"
                            onClick={handleGeneratePassword}
                            title={t("generate_password") || "Generate password"}
                          >
                            <i className="lni lni-refresh-circle-1-clockwise"></i>
                            <span>{t("generate") || "Generate"}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {isEditing && canChangeAnyUserPassword && (
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="form-label">{t("password")}</label>
                    <div className="employeesPasswordDesktop">
                      <div className="input-group">
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control"
                          name="hashed_pass"
                          value={formData.hashed_pass || ""}
                          onChange={handleChange}
                          placeholder={
                            t("leave_blank_to_keep_password") ||
                            "Leave blank to keep current password"
                          }
                        />

                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowPassword((prev) => !prev)}
                          title={
                            showPassword
                              ? (t("hide_password") || "Hide password")
                              : (t("show_password") || "Show password")
                          }
                        >
                          {showPassword ? (
                            <i className="lni lni-locked-2"></i>
                          ) : (
                            <i className="lni lni-unlocked-2"></i>
                          )}
                        </button>

                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={handleGeneratePassword}
                          title={t("generate_password") || "Generate password"}
                        >
                          <i className="lni lni-refresh-circle-1-clockwise"></i>
                        </button>
                      </div>
                    </div>

                    <div className="employeesPasswordMobile">
                      <div className="employeesPasswordBox">
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control employeesPasswordInput"
                          name="hashed_pass"
                          value={formData.hashed_pass || ""}
                          onChange={handleChange}
                          placeholder={
                            t("leave_blank_to_keep_password") ||
                            "Leave blank to keep current password"
                          }
                        />

                        <div className="employeesPasswordActions">
                          <button
                            type="button"
                            className="employeesPasswordActionBtn"
                            onClick={() => setShowPassword((prev) => !prev)}
                            title={
                              showPassword
                                ? (t("hide_password") || "Hide password")
                                : (t("show_password") || "Show password")
                            }
                          >
                            <i
                              className={
                                showPassword
                                  ? "lni lni-locked-2"
                                  : "lni lni-unlocked-2"
                              }
                            ></i>
                            <span>
                              {showPassword
                                ? (t("hide") || "Hide")
                                : (t("show") || "Show")}
                            </span>
                          </button>

                          <button
                            type="button"
                            className="employeesPasswordActionBtn"
                            onClick={handleGeneratePassword}
                            title={t("generate_password") || "Generate password"}
                          >
                            <i className="lni lni-refresh-circle-1-clockwise"></i>
                            <span>{t("generate") || "Generate"}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="col-12 col-lg-9">
                  <label className="form-label">{t("description")}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12 d-flex flex-wrap gap-2 pt-2">
                  {isEditing
                    ? canEditUsers && (
                        <button type="submit" className="btn btn-success" disabled={loadingRoles}>
                          {t("update_employee")}
                        </button>
                      )
                    : canCreateUsers && (
                        <button type="submit" className="btn btn-success" disabled={loadingRoles}>
                          {t("add_employee")}
                        </button>
                      )}
                  {isEditing && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleCancelEdit}
                    >
                      {t("cancel")}
                    </button>
                  )}
                </div>
              </div>
            </form>

            {success && <div className="alert alert-success mt-3 mb-0">{success}</div>}
            {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
          </div>
        </div>
      )}
      <div className="card employeesListCard">
        <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div>
            <div style={{ fontWeight: 800 }}>{t("search")}</div>
            <div className="text-muted small">{t("employees_list_hint")}</div>
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2">
            <select
              title={t("entries_per_page")}
              className="form-select"
              value={clientsPerPage}
              onChange={handleClientsPerPageChange}
              style={{ width: 92 }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
            </select>

            <button
              className="btn btn-primary"
              onClick={() => {
                void refreshData();
              }}
              disabled={loadingClients || loadingRoles}
            >
              {t("refresh")}
            </button>
          </div>
        </div>

        <div className="card-body">
          <div className="row g-2 align-items-end mb-3 employeesDesktopSearch">
            <div className="col-12 col-lg-8">
              <label className="form-label">{t("search")}</label>
              <input
                type="text"
                className="form-control"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={t("search_placeholder")}
              />
            </div>
          </div>

          {sortedClients.length > 0 ? (
            <>
              <div className="employeesMobileTableCard">
                <div className="employeesMobileTableToolbar">
                  <div className="employeesMobileToolbarTitle">
                    {t("table_filters", { defaultValue: "Table filters" })}
                  </div>

                  <div className="employeesMobileToolbarActions">
                    <input
                      type="text"
                      className="form-control"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      placeholder={t("search_placeholder")}
                    />

                    <button
                      className="btn btn-outline-primary"
                      type="button"
                      title={t("refresh")}
                      onClick={() => {
                        void refreshData();
                      }}
                      disabled={loadingClients || loadingRoles}
                    >
                      <i className="lni lni-refresh-circle-1-clockwise"></i>
                    </button>
                  </div>
                </div>

                <div className="employeesMobileTableMeta">
                  <select
                    className="form-select"
                    value={clientsPerPage}
                    onChange={handleClientsPerPageChange}
                    title={t("entries_per_page")}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                  </select>

                  <div>{showingText}</div>
                </div>

                <div className="employeesMobileTableScroll">
                  <table className="employeesMobileTable">
                    <thead>
                      <tr>
                        <th>{t("userId")}</th>
                        <th>{t("employees")}</th>
                      </tr>
                      <tr>
                        <th>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={t("userId")}
                            value={searchQuery}
                            onChange={handleSearchChange}
                          />
                        </th>
                        <th>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={t("search_placeholder")}
                            value={searchQuery}
                            onChange={handleSearchChange}
                          />
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {currentClients.map((client) => (
                        <tr key={client.userId || client.ui_id || client.email}>
                          <td className="employeesMobileIdCell">{client.ui_id}</td>
                          <td>
                            <div className="employeesMobileEmployeeCell">
                              <div className="employeesMobileEmployeeTop">
                                <strong>
                                  {[client.first_name, client.last_name].filter(Boolean).join(" ") ||
                                    client.email ||
                                    "-"}
                                </strong>
                                <span className="employeesMobileRole">
                                  {client.role || t("n_a")}
                                </span>
                              </div>

                              <div className="employeesMobileEmployeeMeta">
                                <span>{t("email")}: <b>{client.email || t("n_a")}</b></span>
                                <span>{t("phone")}: <b>{client.phone || t("n_a")}</b></span>
                                <span>{t("country")}: <b>{client.country || t("n_a")}</b></span>
                                <span>{t("city")}: <b>{client.city || t("n_a")}</b></span>
                                <span>
                                  {t("created_at")}:{" "}
                                  <b>{client.created_at ? new Date(client.created_at).toLocaleString() : "-"}</b>
                                </span>
                                <span>
                                  {t("last_date_connection")}:{" "}
                                  <b>{client.last_date_connection ? new Date(client.last_date_connection).toLocaleString() : "-"}</b>
                                </span>
                              </div>

                              {canEditUsers && (
                                <div className="employeesMobileEmployeeActions">
                                  <button
                                    className="btn btn-warning btn-sm"
                                    type="button"
                                    onClick={() => handleEdit(client)}
                                  >
                                    {t("edit")}
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}

                      {currentClients.length === 0 && !loadingClients && (
                        <tr>
                          <td colSpan={2} className="employeesMobileEmptyCell">
                            {t("no_employees_found")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="table-scroll employeesDesktopTable">
                <table className="table table-striped table-hover mb-0">
                  <thead className="thead-dark">
                    <tr>
                      <th onClick={() => sortData("userId")}>
                        {t("userId")}{" "}
                        {sortConfig.key === "userId" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>
                      <th onClick={() => sortData("first_name")}>
                        {t("first_name")}{" "}
                        {sortConfig.key === "first_name" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>
                      <th onClick={() => sortData("last_name")}>
                        {t("last_name")}{" "}
                        {sortConfig.key === "last_name" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>
                      <th onClick={() => sortData("email")}>
                        {t("email")}{" "}
                        {sortConfig.key === "email" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>
                      <th onClick={() => sortData("phone")}>
                        {t("phone")}{" "}
                        {sortConfig.key === "phone" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>
                      <th onClick={() => sortData("created_at")}>
                        {t("created_at")}{" "}
                        {sortConfig.key === "created_at" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>
                      <th onClick={() => sortData("last_date_connection")}>
                        {t("last_date_connection")}{" "}
                        {sortConfig.key === "last_date_connection" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>
                      <th onClick={() => sortData("date_of_birth")}>
                        {t("date_of_birth")}{" "}
                        {sortConfig.key === "date_of_birth" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>
                      <th onClick={() => sortData("role")}>
                        {t("role")}{" "}
                        {sortConfig.key === "role" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>
                      <th onClick={() => sortData("country")}>
                        {t("country")}{" "}
                        {sortConfig.key === "country" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>
                      <th onClick={() => sortData("city")}>
                        {t("city")}{" "}
                        {sortConfig.key === "city" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>
                      <th onClick={() => sortData("description")}>
                        {t("description")}{" "}
                        {sortConfig.key === "description" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>
                      <th style={{ width: 140 }}>{t("actions")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentClients.map((client) => (
                      <tr key={client.userId || client.ui_id || client.email}>
                        <td>{client.ui_id}</td>
                        <td>{client.first_name}</td>
                        <td>{client.last_name}</td>
                        <td>{client.email}</td>
                        <td>{client.phone || t("n_a")}</td>
                        <td>
                          {client.created_at
                            ? new Date(client.created_at).toLocaleString()
                            : "-"}
                        </td>
                        <td>
                          {client.last_date_connection
                            ? new Date(client.last_date_connection).toLocaleString()
                            : "-"}
                        </td>
                        <td>
                          {client.date_of_birth
                            ? new Date(client.date_of_birth).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })
                            : "-"}
                        </td>
                        <td>{client.role}</td>
                        <td>{client.country}</td>
                        <td>{client.city}</td>
                        <td>{client.description || t("n_a")}</td>
                        <td>
                          {canEditUsers && (
                            <button
                              className="btn btn-warning btn-sm"
                              onClick={() => handleEdit(client)}
                            >
                              {t("edit")}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="employeesPaginationFooter d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
                <div className="text-muted">{showingText}</div>

                <nav aria-label={t("page_navigation")}>
                  <ul className="pagination mb-0 employeesPagination">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" title={t("previous")} aria-label={t("previous")} onClick={() => paginate(currentPage - 1)}>
                        <i className="lni lni-arrow-left"></i>
                      </button>
                    </li>

                    {Array.from({ length: totalPages }, (_, idx) => (
                      <li
                        key={idx}
                        className={`page-item ${currentPage === idx + 1 ? "active" : ""}`}
                      >
                        <button className="page-link" onClick={() => paginate(idx + 1)}>
                          {idx + 1}
                        </button>
                      </li>
                    ))}

                    <li
                      className={`page-item ${
                        currentPage === totalPages || totalPages === 0 ? "disabled" : ""
                      }`}
                    >
                      <button className="page-link" title={t("next")} aria-label={t("next")} onClick={() => paginate(currentPage + 1)}>
                        <i className="lni lni-arrow-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </>
          ) : (
            <p className="text-warning mb-0">{t("no_employees_found")}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Employees;